import { ReactNode } from "react";
import classNames from "classnames";

import BaseModal from "views/uikit/VLModal/BaseModal";
import VLButton from "views/uikit/Buttons/VLButton";
import styles from "./ScanImport.module.scss";

interface AddDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subTitle: string;
  children: ReactNode;
  /** Left of the footer buttons, e.g. what is selected. */
  footerInfo?: ReactNode;
  /** Why "Add" is unavailable; empty when it is available. */
  addDisabledReason?: string;
  addLoading?: boolean;
  /** Either a click handler, or the id of a form that the button submits. */
  onAdd?: () => void;
  submitFormId?: string;
  /**
   * Hold the height steady instead of fitting the content. For content that changes
   * size as it loads or expands, like the scan-results grid; a short form stays compact.
   */
  fixedHeight?: boolean;
}

/**
 * The frame both "Add data" dialogs share, so the two read as one pattern: the same
 * width, and the same footer - Cancel, and "Add" at the bottom right. Content past the
 * available height scrolls inside, between a header and footer that stay put.
 */
export default function AddDataModal({
  isOpen,
  onClose,
  title,
  subTitle,
  children,
  footerInfo,
  addDisabledReason = "",
  addLoading,
  onAdd,
  submitFormId,
  fixedHeight = false,
}: AddDataModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="general"
      size="large"
      className={classNames(styles.addDataModal, {
        [styles.fixedHeight]: fixedHeight,
      })}
      headers={{ title, subTitle }}
      footer={
        <div className={styles.modalFooter}>
          <span className={styles.footerInfo}>{footerInfo}</span>
          <VLButton
            type="default"
            size="middle"
            text="Cancel"
            onClick={onClose}
          />
          <VLButton
            type="primary"
            size="middle"
            text="Add"
            disabled={!!addDisabledReason}
            tooltip={addDisabledReason}
            loading={addLoading}
            onClick={onAdd}
            htmlType={submitFormId ? "submit" : "button"}
            form={submitFormId}
          />
        </div>
      }
    >
      <div className={styles.modalBody}>{children}</div>
    </BaseModal>
  );
}
